// UseGrnItemsTable hook
import * as React from "react";
import { useState, useEffect } from "react";
import { externalgetApi } from "../services/externalApis";
import { SETTING_API_13, SUPPLIER_API_04 } from "../api/ApiModel";
import { CON_STATUS_ACTIVE, CON_TAX_PAGE_CODE } from "../utils/sys/Constants";
import { useSelector } from "react-redux";
import { calculateGrnTotals } from "./GrnCalculate";
import { GrnItem, AnyForNow } from "../features/grn/types";

// Interface for the hook's return type
interface UseGrnItemsTableReturn {
  // State variables and setters
  rows: AnyForNow[];
  setRows: React.Dispatch<React.SetStateAction<AnyForNow[]>>;
  grnItems: GrnItem[];
  setGrnItems: React.Dispatch<React.SetStateAction<GrnItem[]>>;
  supplierItems: AnyForNow[];
  setSupplierItems: React.Dispatch<React.SetStateAction<AnyForNow[]>>;
  selectedSupplier: AnyForNow | null;
  setSelectedSupplier: React.Dispatch<React.SetStateAction<AnyForNow | null>>;
  expanded: boolean[];
  setExpanded: React.Dispatch<React.SetStateAction<boolean[]>>;
  popupType: string | null;
  setPopupType: React.Dispatch<React.SetStateAction<string | null>>;
  fileName: string;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  documentType: string;
  setDocumentType: React.Dispatch<React.SetStateAction<string>>;
  reasonForReje: string;
  setReasonForReje: React.Dispatch<React.SetStateAction<string>>;
  errors: Record<number, Record<string, boolean>>;
  setErrors: React.Dispatch<React.SetStateAction<Record<number, Record<string, boolean>>>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  showRejectItems: Record<string, boolean>;
  setShowRejectItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  showFocItems: Record<string, boolean>;
  setShowFocItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  showItemAttachments: Record<string, boolean>;
  setShowItemAttachments: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  rejectItems: AnyForNow[];
  setRejectItems: React.Dispatch<React.SetStateAction<AnyForNow[]>>;
  attachments: AnyForNow[];
  setAttachments: React.Dispatch<React.SetStateAction<AnyForNow[]>>;
  grnTaxTypes: AnyForNow[];
  setGrnTaxTypes: React.Dispatch<React.SetStateAction<AnyForNow[]>>;
  taxValues: Record<string, AnyForNow>;
  setTaxValues: React.Dispatch<React.SetStateAction<Record<string, AnyForNow>>>;
  totalDiscount: number;
  totalTax: number;
  disableBillLevelTax: boolean;

  // Functions
  addItemToTable: (selectedItem: AnyForNow) => void;
  setTotalDiscount: React.Dispatch<React.SetStateAction<number>>;
  setTotalTax: React.Dispatch<React.SetStateAction<number>>;
  updateRowField: (rowId: AnyForNow, fieldName: string, value: AnyForNow) => void;
  toggleExpand: (index: number) => void;
  handleAddNewItem: (event: AnyForNow, selectedItem: AnyForNow) => void;
  setNewItem: React.Dispatch<React.SetStateAction<AnyForNow | null>>;
  toggleRejectItems: (itemId: string) => void;
  toggleFocItems: (itemId: string) => void;
  toggleItemAttachments: (itemId: string) => void;
  handleItemChange: (index: number, field: string, value: AnyForNow) => void;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  validateAllItems: () => boolean;
  removeItemFromTable: (indexToRemove: number) => void;
  validateItem: (item: AnyForNow) => Record<string, boolean>;
  handleClearAttachments: (itemId: string) => void;
  handleClearFocItems: (itemId: string, index: number) => void;
  handleClearRejectItems: (itemId: string, index: number) => void;
}

export function UseGrnItemsTable(
  initialDataGrnItemsTable: GrnItem[] = [],
  initialSupplier: AnyForNow | null = null
): UseGrnItemsTableReturn {
  const [rows, setRows] = useState<AnyForNow[]>(
    initialDataGrnItemsTable.map((item, idx) => ({
      ...item,
      id: item.id || idx + 1,
      focItems: [],
      itemAttachments: {
        doctypecode: "",
        docpathjson: "",
        filename: "",
        bytarr: "",
        remark: "",
      },

      rejectItem: {
        returnedqty: "",
        reason: "",
        descrip: "",
        supproductcode: 0,
        attachments: item.rejectItem?.attachments || [], // ensure array
        doctypecode: item.rejectItem?.doctypecode || "",
        docpathjson: item.rejectItem?.docpathjson || "",
        filename: item.rejectItem?.filename || "",
        bytarr: item.rejectItem?.bytarr || "",
        remark: item.rejectItem?.remark || "",
      },
    }))
  );

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};
  const [rejectItems, setRejectItems] = useState<AnyForNow[]>(
    initialDataGrnItemsTable || []
  );
  const [grnTaxTypes, setGrnTaxTypes] = useState<AnyForNow[]>([]);
  const [taxValues, setTaxValues] = useState<Record<string, AnyForNow>>({});
  const [grnItems, setGrnItems] = useState<GrnItem[]>(
    initialDataGrnItemsTable.map((item) => ({
      ...item,

      isAdded: true,
      rejectItem: item.rejectItem || {
        returnedqty: "",
        reason: "",
        descrip: "",
        supproductcode: 0,
        attachments: [],
        doctypecode: "",
        docpathjson: "",
        filename: "",
        bytarr: "",
        remark: "",
      },
      itemAttachments: item.itemAttachments || {
        doctypecode: "",
        docpathjson: "",
        docothername:"",
        filename: "",
        bytarr: "",
        remark: "",
      },
    }))
  );

  const [supplierItems, setSupplierItems] = useState<AnyForNow[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<AnyForNow | null>(initialSupplier);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [attachments, setAttachments] = useState<AnyForNow[]>([]);
  const [popupType, setPopupType] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("No file selected");
  const [documentType, setDocumentType] = useState<string>("");
  const [reasonForReje, setReasonForReje] = useState<string>("");
  const [errors, setErrors] = useState<Record<number, Record<string, boolean>>>({});
  const [inputValue, setInputValue] = useState<string>("");
  const [newItem, setNewItem] = useState<AnyForNow | null>(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [showRejectItems, setShowRejectItems] = useState<Record<string, boolean>>({});
  const [showFocItems, setShowFocItems] = useState<Record<string, boolean>>({});
  const [showItemAttachments, setShowItemAttachments] = useState<Record<string, boolean>>({});
  const [totalTax, setTotalTax] = useState<number>(0);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);

  // Initialize visibility states based on existing data in initialDataGrnItemsTable
  // This ensures that when navigating back (e.g., from ViewDetailGrn), sections with data are visible
  useEffect(() => {
    if (initialDataGrnItemsTable && initialDataGrnItemsTable.length > 0) {
      const rejectVisibility: Record<string, boolean> = {};
      const focVisibility: Record<string, boolean> = {};
      const attachmentVisibility: Record<string, boolean> = {};

      initialDataGrnItemsTable.forEach((item) => {
        const itemId = String(item.id || item.supproductcode || item.productcode);

        // Check if item has reject item data
        const hasRejectData = item.rejectItem && (
          (Number(item.rejectItem.returnedqty) > 0) ||
          (item.rejectItem.reason && item.rejectItem.reason.trim() !== "") ||
          (item.rejectItem.descrip && item.rejectItem.descrip.trim() !== "") ||
          (item.rejectItem.attachments && item.rejectItem.attachments.length > 0)
        );
        rejectVisibility[itemId] = !!hasRejectData;

        // Check if item has FOC data
        const hasFocData = (
          (Number(item.freeqty) > 0) ||
          (item.descrip && item.descrip.trim() !== "")
        );
        focVisibility[itemId] = !!hasFocData;

        // Check if item has attachment data
        // itemAttachments can be either an object (ItemAttachments) or an array (Attachment[])
        let hasAttachmentData = false;
        if (item.itemAttachments) {
          if (Array.isArray(item.itemAttachments)) {
            // If it's an array, check if it has any items
            hasAttachmentData = item.itemAttachments.length > 0;
          } else {
            // If it's an object, check for filename, bytarr, or nested attachments array
            hasAttachmentData = (
              (item.itemAttachments.filename && item.itemAttachments.filename.trim() !== "") ||
              (item.itemAttachments.bytarr && item.itemAttachments.bytarr.trim() !== "") ||
              (item.itemAttachments.attachments && item.itemAttachments.attachments.length > 0)
            );
          }
        }
        attachmentVisibility[itemId] = hasAttachmentData;
      });

      setShowRejectItems(rejectVisibility);
      setShowFocItems(focVisibility);
      setShowItemAttachments(attachmentVisibility);
    }
  }, []); // Run only on mount with initial data

  // Update visibility states when grnItems changes (e.g., when data is restored from localStorage)
  // This ensures sections with data are visible after data restoration
  useEffect(() => {
    if (grnItems && grnItems.length > 0) {
      setShowRejectItems((prevState) => {
        const newState = { ...prevState };
        grnItems.forEach((item) => {
          const itemId = String(item.id || item.supproductcode || item.productcode);
          // Only set to true if item has data and visibility hasn't been explicitly set yet
          if (!(itemId in newState)) {
            const hasRejectData = item.rejectItem && (
              (Number(item.rejectItem.returnedqty) > 0) ||
              (item.rejectItem.reason && item.rejectItem.reason.trim() !== "") ||
              (item.rejectItem.descrip && item.rejectItem.descrip.trim() !== "") ||
              (item.rejectItem.attachments && item.rejectItem.attachments.length > 0)
            );
            newState[itemId] = !!hasRejectData;
          } else if (!newState[itemId]) {
            // If visibility is false, check if data now exists and update
            const hasRejectData = item.rejectItem && (
              (Number(item.rejectItem.returnedqty) > 0) ||
              (item.rejectItem.reason && item.rejectItem.reason.trim() !== "") ||
              (item.rejectItem.descrip && item.rejectItem.descrip.trim() !== "") ||
              (item.rejectItem.attachments && item.rejectItem.attachments.length > 0)
            );
            if (hasRejectData) {
              newState[itemId] = true;
            }
          }
        });
        return newState;
      });

      setShowFocItems((prevState) => {
        const newState = { ...prevState };
        grnItems.forEach((item) => {
          const itemId = String(item.id || item.supproductcode || item.productcode);
          if (!(itemId in newState)) {
            const hasFocData = (
              (Number(item.freeqty) > 0) ||
              (item.descrip && item.descrip.trim() !== "")
            );
            newState[itemId] = !!hasFocData;
          } else if (!newState[itemId]) {
            const hasFocData = (
              (Number(item.freeqty) > 0) ||
              (item.descrip && item.descrip.trim() !== "")
            );
            if (hasFocData) {
              newState[itemId] = true;
            }
          }
        });
        return newState;
      });

      setShowItemAttachments((prevState) => {
        const newState = { ...prevState };
        grnItems.forEach((item) => {
          const itemId = String(item.id || item.supproductcode || item.productcode);

          // Helper function to check if item has attachment data
          // itemAttachments can be either an object (ItemAttachments) or an array (Attachment[])
          const checkHasAttachmentData = (): boolean => {
            if (!item.itemAttachments) return false;
            if (Array.isArray(item.itemAttachments)) {
              return item.itemAttachments.length > 0;
            }
            return (
              (item.itemAttachments.filename && item.itemAttachments.filename.trim() !== "") ||
              (item.itemAttachments.bytarr && item.itemAttachments.bytarr.trim() !== "") ||
              (item.itemAttachments.attachments && item.itemAttachments.attachments.length > 0)
            );
          };

          if (!(itemId in newState)) {
            newState[itemId] = checkHasAttachmentData();
          } else if (!newState[itemId]) {
            if (checkHasAttachmentData()) {
              newState[itemId] = true;
            }
          }
        });
        return newState;
      });
    }
  }, [grnItems]);

  // Track last fetched supplier to prevent repeated fetches
  const lastFetchedSupplierRef = React.useRef<string | null>(null);

  // Compute if bill level tax should be disabled (when items have different tax rates)
  const disableBillLevelTax = grnItems.length > 0 &&
    grnItems.some((item, index, arr) =>
      arr.some(otherItem => (item.taxrate || item.taxcode) !== (otherItem.taxrate || otherItem.taxcode))
    );

  const toggleRejectItems = (itemId: string): void => {
    setShowRejectItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };
  const toggleFocItems = (itemId: string): void => {
    setShowFocItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };
  const toggleItemAttachments = (itemId: string): void => {
    setShowItemAttachments((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

 
  const handleClearRejectItems = (itemId: string, index: number): void => {
    setShowRejectItems((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    setGrnItems((prev) => {
      if (index === undefined || !prev[index]) return prev; // guard clause

      const updated = [...prev];
      if (!updated[index].rejectItem) return updated;

      updated[index].rejectItem = {
        ...updated[index].rejectItem,
        returnedqty: 0,
        reason: "",
        descrip: "",
        attachments: [],
        remark: "",
        // Also clear docpathjson to ensure attachments are not shown in ViewDetailGrn
        docpathjson: "",
      };

      return updated;
    });
  };

 
  const handleClearFocItems = (itemId: string, index: number): void => {
    // Hide the FOC Items section for this item
    setShowFocItems((prev) => ({
      ...prev,
      [itemId]: false,
    }));

    // Clear FOC fields in grnItems
    setGrnItems((prev) => {
      if (index === undefined || !prev[index]) return prev;

      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        freeqty: 0,
        descrip: "",
      };

      return updated;
    });
  };

  const handleClearAttachments = (itemId: string): void => {
    setShowItemAttachments((prev) => ({
      ...prev,
      [itemId]: false,
    }));
    setGrnItems((prev) => {
      return prev.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            selectedDocumentTypeCode: "",
            doctypeothername: "",
            itemAttachments: {
              attachments: [],
              doctypecode: "",
              docpathjson: "",
              filename: "",
              bytarr: "",
              remark: "",
            },
          };
        }
        return item;
      });
    });
    console.log("Clearing attachments for itemId:", itemId);
    console.log("ITEMSSSSSSSSSSSS:", showItemAttachments);
  };

  const validateItem = (item: AnyForNow): Record<string, boolean> => {
    const itemErrors: Record<string, boolean> = {};
    const numericFields = ["poqty", "receivedqty", "unitprice", "acceptedqty"];

    numericFields.forEach((field) => {
      const value = item[field];
      // Check if value is empty, null, or undefined
      if (value === undefined || value === null || value === "") {
        itemErrors[field] = true;
      } else {
        // Convert to string first to handle numeric strings, then remove commas/spaces
        const stringValue = String(value).replace(/,/g, "").trim();
        // Check if empty after cleaning
        if (stringValue === "") {
          itemErrors[field] = true;
        } else {
          const num = Number(stringValue);
          // Validate: must be a valid number, >= 0, and for discountrate <= 100
          // For unitprice, must be > 0 (not 0 or negative)
          if (isNaN(num) || num < 0 || (field === "discountrate" && num > 100)) {
            itemErrors[field] = true;
          } else if (field === "unitprice" && num === 0) {
            itemErrors[field] = true;
          }
        }
      }
    });

    // Validate batch number - handle both string and non-string values
    // const batchno = item.batchno;
    // if (!batchno || (typeof batchno === "string" && batchno.trim() === "")) {
    //   itemErrors.batchno = true;
    // }

    // Validate manufacture date
    // if (!item.manufacdate || item.manufacdate === "") {
    //   itemErrors.manufacdate = true;
    // }

    return itemErrors;
  };

  const validateAllItems = (): boolean => {
    const newErrors: Record<number, Record<string, boolean>> = {};
    let hasError = false;

    grnItems.forEach((item, index) => {
      const itemErrors = validateItem(item);
      if (Object.keys(itemErrors).length > 0) {
        hasError = true;
        newErrors[index] = itemErrors;
      }
    });

    setErrors(newErrors);
    return hasError;
  };

  const handleItemChange = (index: number, field: string, value: AnyForNow): void => {
    const updatedItems = [...grnItems];
    // Create a new object reference for the modified item to ensure React detects the change
    updatedItems[index] = { ...updatedItems[index] };

    if (field === "taxrate") {
      updatedItems[index].taxrate = value;
    } else {
      updatedItems[index][field] = value;
    }

    // Calculate quantity difference if needed
    const receivedQty = Number(updatedItems[index].receivedqty) || 0;
    const rejectedQty =
      Number(updatedItems[index].rejectItem?.returnedqty) || 0;
    const freeQty = Number(updatedItems[index].freeqty) || 0;
    const acceptedQty = receivedQty - rejectedQty;
    updatedItems[index].acceptedqty = acceptedQty >= 0 ? acceptedQty : 0;

    const poQty = Number(updatedItems[index].poqty) || 0;
    updatedItems[index].quantitydifference = poQty - acceptedQty;

    // Keep dependent quantities in sync
    const billQty = Math.max(acceptedQty - freeQty, 0);
    updatedItems[index].billqty = billQty;
    updatedItems[index].totalqty = acceptedQty + freeQty;
    updatedItems[index].pendingpoqty = Math.max(poQty - acceptedQty, 0);

    // Calculate Value and Tax Value using the same logic as GrnCalculate.ts
    if (
      [
        "acceptedqty",
        "receivedqty",
        "unitprice",
        "returnedqty",
        "freeqty",
        "taxrate",
        "discountrate",
        "poqty",
      ].includes(field)
    ) {
      // Use the calculated acceptedQty (which is already updated in the item)
      // This ensures we use the most up-to-date value
      const currentAcceptedQty = acceptedQty >= 0 ? acceptedQty : 0;
      const currentUnitPrice = Number(updatedItems[index].unitprice) || 0;
      const currentDiscountRate = Number(updatedItems[index].discountrate) || 0;
      const currentTaxRate = Number(updatedItems[index].taxrate) || 0;

      // Calculate item value: acceptedQty * unitPrice
      const itemValue = currentAcceptedQty * currentUnitPrice;
      
      // Calculate discount amount
      const discountAmount = (itemValue * currentDiscountRate) / 100;
      
      // Calculate value after discount
      const grossAfterDiscount = itemValue - discountAmount;
      
      // Calculate tax value on the amount after discount
      const taxValue = (grossAfterDiscount * currentTaxRate) / 100;

      // Update the item with calculated values
      // grossValue is set to itemValue (as per GrnCalculate.ts line 79)
      updatedItems[index].grossvalue = parseFloat(itemValue.toFixed(2));
      updatedItems[index].itemvalue = parseFloat(itemValue.toFixed(2)); // Value field displays itemvalue
      updatedItems[index].taxvalue = parseFloat(taxValue.toFixed(2));
      updatedItems[index].discountvalue = parseFloat(discountAmount.toFixed(2));
    }

    setGrnItems(updatedItems);

    // Clear errors for this field if exists
    setErrors((prevErrors) => {
      if (prevErrors[index]?.[field]) {
        const newErrors = { ...prevErrors };
        if (newErrors[index]) {
          delete newErrors[index][field];
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      }
      return prevErrors;
    });
  };

  // Fetch items when supplier is selected
  useEffect(() => {
    if (selectedSupplier && selectedSupplier.optSupplierRefCode) {
      // Ensure we have a valid supplier reference code
      const supplierRefCode = selectedSupplier.optSupplierRefCode.toString().trim();

      // Only fetch if this is a different supplier than last time
      if (supplierRefCode && supplierRefCode !== lastFetchedSupplierRef.current) {
        fetchSupplierItems(supplierRefCode);
        lastFetchedSupplierRef.current = supplierRefCode;
      } else if (!supplierRefCode) {
        setSupplierItems([]);
        lastFetchedSupplierRef.current = null;
      }
    } else {
      setSupplierItems([]);
      lastFetchedSupplierRef.current = null;
      // Only clear grnItems if we don't have initial data (not in edit mode)
      if (!initialDataGrnItemsTable || initialDataGrnItemsTable.length === 0) {
        setGrnItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier]);

  useEffect(() => {
    fetchTaxType();
  }, []);
  // Supplierproducts to get supplier items
  const fetchSupplierItems = async (supplierRefCode: string): Promise<void> => {
    if (!supplierRefCode) {
      setSupplierItems([]);
      return;
    }
    try {
      const params = { SUPREFCODE: supplierRefCode, STS: CON_STATUS_ACTIVE };
      const response = await externalgetApi(SUPPLIER_API_04, "GET", params);
      // Ensure response is an array
      const items = Array.isArray(response) ? response : (response ? [response] : []);
      setSupplierItems(items);
    } catch (error) {
      console.error("Error fetching supplier items:", error);
      setSupplierItems([]);
    }
  };
  const fetchTaxType = async (): Promise<void> => {
    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        TAXMPGCODE: CON_TAX_PAGE_CODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(SETTING_API_13, "GET", params);
      setGrnTaxTypes(response);

      console.log("tax types", response);
    } catch (error) {
      console.error("Error fetching GRN document types", error);
    }
  };
  // Add item to GRN items table
  const addItemToTable = (selectedItem: AnyForNow): void => {
    // Check if item has any valid identifier
    if (!selectedItem || (!selectedItem.productcode && !selectedItem.id && !selectedItem.supproductcode)) {
      return;
    }

    // Use supproductcode as primary identifier for supplier items, fallback to productcode or id
    const uniqueId =
      selectedItem?.supproductcode || selectedItem?.id || selectedItem?.productcode || Date.now();

    const isExist = grnItems.some(
      (item) => 
        (item.id && item.id === uniqueId) || 
        (item.productcode && item.productcode === uniqueId) ||
        (item.supproductcode && item.supproductcode === uniqueId) ||
        (item.productcode && selectedItem.productcode && item.productcode === selectedItem.productcode) ||
        (item.supproductcode && selectedItem.supproductcode && item.supproductcode === selectedItem.supproductcode)
    );
    if (isExist) {
      return;
    }

    const newItem = {
      ...selectedItem,
      id: uniqueId,
      isAdded: false,
      istax: selectedItem.istax || "N", // copy istax
      taxcode: selectedItem.taxcode || "", // copy taxcode
      taxrate: "0",
      taxtype: selectedItem.taxtype || "",
      // taxrate: "",
      acceptedqty: "",
      poqty: "",
      unitprice: "0",
      receivedqty: "",
      discountvalue: "",
      discountrate: "0",
      freeqty: "",
      returnedqty: "",
      taxvalue: 0,
      grossvalue: 0,
      itemvalue: 0,
      //newly added fields
      aftertaxvalue: 0,
      billqty: 0,
      descrip:"",
      expdate: "",
      pendingpoqty:"",
      podate: "",
      perunitaftertaxvalue:  0, //new *
  perunitdiscountvalue:  0, //new *
  perunittaxableamount:  0, //new *
  perunittaxvalue: 0, //new*
taxableamount: 0, //new
unitdistribution: 0, //new
unitpricewithfree: 0, //new *

    };
    // newItem.taxvalue = calculateTaxAmount(newItem);
    // newItem.grossvalue = calculateItemValue(newItem);
    setGrnItems((prev) => [newItem, ...prev]);
    setRows((prev) => [...prev, newItem]);
    setShowRejectItems((prev) => ({
      ...prev,
      [String(newItem.id)]: false,
    }));
    setShowFocItems((prev) => ({
      ...prev,
      [String(newItem.id)]: false,
    }));

    setShowItemAttachments((prev) => ({
      ...prev,
      [String(newItem.id)]: false,
    }));

    setExpanded((prev) => [true, ...prev.map(() => false)]);
    setNewItem(null);
    setInputValue("");
  };

  const updateRowField = (rowId: AnyForNow, fieldName: string, value: AnyForNow): void => {
    setGrnItems((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [fieldName]: value } : row
      )
    );
  };

  // Add item to GRN items table

  const handleAddNewItem = (event: AnyForNow, selectedItem: AnyForNow): void => {
    if (selectedItem) {
      addItemToTable(selectedItem); // No need to pass event
      setNewItem(null);
      setInputValue("");
    }
  };

  const removeItemFromTable = (indexToRemove: number): void => {
    setGrnItems((prevItems) => {
      const newItems = prevItems.filter((_, idx) => idx !== indexToRemove);
      return newItems;
    });

    // Also clear errors for that item if any
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[indexToRemove];
      // Adjust error keys for items after removed index
      const adjustedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const keyNum = Number(key);
        adjustedErrors[keyNum > indexToRemove ? keyNum - 1 : keyNum] =
          newErrors[key];
      });
      return adjustedErrors;
    });

    // Remove keys from showRejectItems, showFocItems, showItemAttachments by item id
    setGrnItems((prevItems) => {
      // Get the id of the item being removed
      const removedItemId = prevItems[indexToRemove]?.id;

      // Remove keys from the show states
      setShowRejectItems((prev) => {
        const newState = { ...prev };
        if (removedItemId in newState) delete newState[removedItemId];
        return newState;
      });
      setShowFocItems((prev) => {
        const newState = { ...prev };
        if (removedItemId in newState) delete newState[removedItemId];
        return newState;
      });
      setShowItemAttachments((prev) => {
        const newState = { ...prev };
        if (removedItemId in newState) delete newState[removedItemId];
        return newState;
      });

      return prevItems;
    });

    setExpanded((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const toggleExpand = (index: number): void => {
    setExpanded((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  return {
    rows,
    setRows,
    grnItems,
    setGrnItems,
    supplierItems,
    setSupplierItems,
    selectedSupplier,
    setSelectedSupplier,
    expanded,
    setExpanded,
    popupType,
    setPopupType,
    fileName,
    setFileName,
    documentType,
    setDocumentType,
    reasonForReje,
    setReasonForReje,
    errors,
    setErrors,
    addItemToTable,
    setTotalDiscount,
    updateRowField,
    toggleExpand,
    inputValue,
    setInputValue,
    handleAddNewItem,
    setNewItem,
    toggleRejectItems,
    toggleFocItems,
    toggleItemAttachments,
    showFocItems,
    showRejectItems,
    showItemAttachments,
    handleItemChange,
    setSearchInput,
    rejectItems,
    setRejectItems,
    attachments,
    setAttachments,
    validateAllItems,
    removeItemFromTable,
    validateItem,
    grnTaxTypes,
    setGrnTaxTypes,
    // calculateTaxAmount,
    // calculateGrossAmount,
    setShowItemAttachments,
    setShowFocItems,
    setShowRejectItems,
    handleClearAttachments,
    handleClearFocItems,
    handleClearRejectItems,
    // calculateItemValue,
    taxValues,
    setTaxValues,
    totalDiscount,
    totalTax,
    setTotalTax,
    disableBillLevelTax,
    // calculateTotals,
  };
}
